'use client'

import Link from 'next/link'
import { Icon } from '@/components/icon'
import { capture } from '@/features/tracking'
import type { GateReason } from './watch-context'

interface PlayerGateProps {
  reason: GateReason
  expiresAt?: string | null
}

function getGateContent(reason: GateReason) {
  switch (reason) {
    case 'expired':
      return {
        icon: 'circle-minus' as const,
        title: 'Access Expired',
        description:
          'Your 48 hour time limit has expired. To continue watching and access extended interviews please Pay it Forward.',
        trackingPoint: 'expiration' as const,
      }
    case 'premium_required':
      return {
        icon: 'lock' as const,
        title: 'Premium Content',
        description:
          'This content requires Premium access. Pay it Forward $35 or more to unlock this extended interview and all premium content.',
        trackingPoint: 'locked_content' as const,
      }
    case 'elite_required':
      return {
        icon: 'lock' as const,
        title: 'Elite Content',
        description:
          'This content requires Elite access. Pay it Forward $500 or more to unlock all exclusive content.',
        trackingPoint: 'locked_content' as const,
      }
    case 'standard_required':
      return {
        icon: 'lock' as const,
        title: 'Exclusive Content',
        description: 'Please Pay it Forward to unlock this content.',
        trackingPoint: 'locked_content' as const,
      }
  }

  const _exhaustive: never = reason
  return _exhaustive
}

export function PlayerGate({ reason, expiresAt }: PlayerGateProps) {
  const content = getGateContent(reason)
  const isExpiration = reason === 'expired'
  const expirationLabel = isExpiration && expiresAt ? new Date(expiresAt).toLocaleString() : null

  return (
    <div className='flex min-h-svh flex-col items-center justify-center bg-black p-4 text-christ-white-900'>
      <div className='flex max-w-screen-640 flex-col items-center text-center'>
        <Icon name={content.icon} className='mb-6 size-12 opacity-80' />

        <h1 className='mb-4 font-formula-condensed text-4xl uppercase tracking-wide md:text-5xl'>
          {content.title}
        </h1>

        <p className='mb-8 max-w-lg text-gray-300 text-lg leading-relaxed'>{content.description}</p>
        {expirationLabel ? (
          <p className='-mt-6 mb-8 text-gray-500 text-sm uppercase tracking-widest'>
            Expired {expirationLabel}
          </p>
        ) : null}

        <div className='flex w-full max-w-sm flex-col gap-4'>
          <Link
            href='/pay-it-forward'
            className='flex w-full items-center justify-center bg-christ-white-900 px-8 py-5 pb-4 text-center font-bold font-formula-condensed text-christ-black-900 text-xl uppercase tracking-widest transition-all hover:opacity-90'
            onClick={() => {
              capture('donation:pay_it_forward_modal_view', {
                trigger_point: content.trackingPoint,
              })
            }}
          >
            Pay It Forward
          </Link>

          <Link
            href={isExpiration ? '/' : '/browse'}
            className='w-full px-8 py-3 text-center font-semibold text-gray-500 text-sm uppercase tracking-widest transition-colors hover:text-white'
          >
            {isExpiration ? 'Home' : 'Back to Browse'}
          </Link>
        </div>
      </div>
    </div>
  )
}

export default function PlayerExpiration() {
  return <PlayerGate reason='expired' />
}
